/**
 * Kinetic type, on DOM text only, through the Web Animations API:
 *
 *   scramble   glyphs run through 0/1 for 280 ms and settle left to right
 *              while the weight climbs from 300 (titles, head hashes)
 *   type       the refusal sentence reveals at about 38 characters a second,
 *              and never takes more than 900 ms (a long detail types faster)
 *   flap       a hex mask flips only the digits that changed, split-flap style
 *
 * Text always goes in as text. While a scramble or a flap runs, the real
 * string sits in a visually hidden span, so a screen reader never reads the
 * noise. Reduced motion, or no `animate`, is a plain swap. The timing maths
 * is pure and tested.
 */

export const SCRAMBLE_MS = 280;
export const TYPE_CPS = 38;
/** §4.4 4b: the sentence is whole within 900 ms, whatever its length. */
export const TYPE_MAX_MS = 900;
export const FLAP_OUT_MS = 70;
export const FLAP_IN_MS = 90;
export const FLAP_STAGGER_MS = 28;
const MAX_KINETIC = 160;   // longer strings just swap

/** How long the typewriter takes over a string: 38 characters a second, capped at 900 ms. */
export const typeMs = (text: string): number => Math.min(TYPE_MAX_MS, Math.round((text.length / TYPE_CPS) * 1000));

/** The indices a split-flap must turn to go from `from` to `to`. */
export const flapChanges = (from: string, to: string): number[] => {
  const out: number[] = [];
  const n = Math.max(from.length, to.length);
  for (let i = 0; i < n; i++) if (from[i] !== to[i]) out.push(i);
  return out;
};

const hash = (a: number, b: number, c: number): number => {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35) ^ Math.imul(c + 1, 0x27d4eb2f);
  h ^= h >>> 15;
  return h >>> 0;
};

/** When glyph `i` of `n` settles, in ms after the scramble starts. */
export const settleMs = (i: number, n: number, dur = SCRAMBLE_MS): number =>
  Math.round(dur * (0.35 + (0.65 * (n <= 1 ? 1 : i / (n - 1)))));

/** The text a scramble shows `elapsed` ms in: settled glyphs are real, the rest flicker 0/1 (spaces stay). */
export const scrambleFrame = (text: string, elapsed: number, seed = 1, dur = SCRAMBLE_MS): string => {
  const n = text.length;
  let out = '';
  for (let i = 0; i < n; i++) {
    const ch = text[i] as string;
    if (ch === ' ' || elapsed >= settleMs(i, n, dur)) out += ch;
    else out += hash(seed, i, Math.floor(elapsed / 40)) & 1 ? '1' : '0';
  }
  return out;
};

export type TypeHow = 'scramble' | 'type' | 'flap';

export interface Typist {
  type(el: HTMLElement, text: string, how: TypeHow): void;
  cancelAll(): void;
}

export const createTypist = (opts: { reduced: () => boolean; now?: () => number }): Typist => {
  const now = opts.now ?? (() => performance.now());
  const running = new WeakMap<HTMLElement, () => void>();
  const live = new Set<() => void>();
  let seed = 1;

  const finish = (el: HTMLElement) => {
    const stop = running.get(el);
    if (stop) stop();
  };

  const plain = (el: HTMLElement, text: string) => {
    finish(el);
    el.textContent = text;
  };

  const srSpan = (text: string): HTMLSpanElement => {
    const s = document.createElement('span');
    s.className = 'fx-sr';
    s.textContent = text;
    return s;
  };

  const track = (el: HTMLElement, stop: () => void) => {
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      stop();
      running.delete(el);
      live.delete(once);
    };
    running.set(el, once);
    live.add(once);
    return once;
  };

  const scramble = (el: HTMLElement, text: string) => {
    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    el.replaceChildren(shown, srSpan(text));
    const s = seed++;
    const t0 = now();
    let raf = 0;
    const timer: { id?: ReturnType<typeof setTimeout> } = {};
    let anim: Animation | null = null;
    let weight = '';
    try { weight = getComputedStyle(el).fontWeight; } catch { weight = ''; }
    if (Number(weight) > 320) {
      try {
        anim = shown.animate([{ fontWeight: '300' }, { fontWeight: weight }], { duration: SCRAMBLE_MS + 160, easing: 'cubic-bezier(.16,1,.3,1)' });
      } catch { anim = null; }
    }
    const stop = track(el, () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer.id);
      anim?.cancel();
      el.textContent = text;
    });
    const step = () => {
      const e = now() - t0;
      if (e >= SCRAMBLE_MS) { stop(); return; }
      shown.textContent = scrambleFrame(text, e, s);
      raf = requestAnimationFrame(step);
    };
    // rAF does not run in a hidden tab: the timer lands the text regardless
    timer.id = setTimeout(stop, SCRAMBLE_MS + 120);
    step();
  };

  const typewriter = (el: HTMLElement, text: string) => {
    el.textContent = text;
    const n = Math.max(1, text.length);
    let anim: Animation | null = null;
    try {
      anim = el.animate([{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], { duration: typeMs(text), easing: `steps(${n}, end)` });
    } catch { anim = null; }
    if (!anim) return;
    const stop = track(el, () => anim?.cancel());
    anim.onfinish = stop;
  };

  const flap = (el: HTMLElement, text: string) => {
    const from = el.textContent ?? '';
    const changed = flapChanges(from, text);
    if (changed.length === 0) return;
    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    const cells: HTMLSpanElement[] = [];
    for (let i = 0; i < Math.max(from.length, text.length); i++) {
      const c = document.createElement('span');
      c.className = 'fx-flap';
      c.textContent = from[i] ?? ' ';
      cells.push(c);
      shown.append(c);
    }
    el.replaceChildren(shown, srSpan(text));
    const anims: Animation[] = [];
    const timers: ReturnType<typeof setTimeout>[] = [];
    const stop = track(el, () => {
      for (const a of anims) a.cancel();
      for (const t of timers) clearTimeout(t);
      el.textContent = text;
    });
    let left = changed.length;
    changed.forEach((i, k) => {
      const c = cells[i] as HTMLSpanElement;
      const delay = k * FLAP_STAGGER_MS;
      try {
        const out = c.animate([{ transform: 'rotateX(0deg)' }, { transform: 'rotateX(90deg)' }], { duration: FLAP_OUT_MS, delay, easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards' });
        anims.push(out);
        out.onfinish = () => {
          c.textContent = text[i] ?? '';
          const back = c.animate([{ transform: 'rotateX(-90deg)' }, { transform: 'rotateX(0deg)' }], { duration: FLAP_IN_MS, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' });
          anims.push(back);
          back.onfinish = () => {
            if (--left === 0) stop();
          };
        };
      } catch {
        stop();
      }
    });
    // whatever happens to the animations (a hidden tab), the digits land
    timers.push(setTimeout(stop, changed.length * FLAP_STAGGER_MS + FLAP_OUT_MS + FLAP_IN_MS + 200));
  };

  return {
    type(el, text, how) {
      const value = String(text);
      if (opts.reduced() || value.length > MAX_KINETIC || typeof el.animate !== 'function') {
        plain(el, value);
        return;
      }
      finish(el);
      if (how === 'scramble') scramble(el, value);
      else if (how === 'type') typewriter(el, value);
      else flap(el, value);
    },
    cancelAll() {
      for (const stop of [...live]) stop();
    },
  };
};
